import { DEFAULT_NODE } from './Grid/Grid';

export const EDGE_STATE = {
  ACTIVE: 'ACTIVE',
  NOT_ALLOWED: 'NOT_ALLOWED',
  HOVERED: 'HOOVERED',
  USERINPUT: 'USERINPUT',
};
// get deep copy of JSON
export const copy = json => JSON.parse(JSON.stringify(json));

// to get multiple styles in space-seperated format
export const multiStyles = (styles, classNames) =>
  classNames
    .reduce(
      (acc, className) => [
        ...acc,
        ...(!className ? [] : [styles?.[className] || className]),
      ],
      [],
    )
    .join(' ');

// to check if number is valid number
export const isValid = (n, lim) => n >= 0 && n <= lim;

// to check all elements are valid numbers
export const areValid = (n, lim) => {
  n.every(n => isValid(n, lim));
  return true;
};

const isValuePresent = (a, b, sol, isAutoCheck) => {
  if (!isAutoCheck) {
    return true;
  }
  return sol.some(node => {
    // Check if 'node.a' matches 'a' AND 'node.b' matches 'b'
    const isAMatch = JSON.stringify(node.a) === JSON.stringify(a);
    const isBMatch = JSON.stringify(node.b) === JSON.stringify(b);

    return isAMatch && isBMatch; // Both must match in the same node
  });
};

export const getMatrix = (
  [dim1, dim2],
  edges = [],
  numbers = [],
  solution = [],
  isAutoCheck = false,
) => {
  const nRows = dim1;
  const nCols = dim2;

  const temp = new Array(dim1 + 1)
    .fill(0)
    .map(() => new Array(dim2 + 1).fill(DEFAULT_NODE));

  if (numbers.length > 0) {
    numbers.forEach(({ r, c, n }) => {
      if (r <= nRows && c <= nCols) {
        temp[r - 1][c - 1] = { ...temp[r - 1][c - 1], n };
      }
      return undefined;
    });
  }

  if (edges.length > 0) {
    edges.forEach(
      ({ a: [sx, sy], b: [ex, ey], notAllowed, hovered, userInput }) => {
        let edgeState;
        if (notAllowed) {
          edgeState = EDGE_STATE.NOT_ALLOWED;
        } else if (hovered) {
          edgeState = EDGE_STATE.HOVERED;
        } else if (userInput) {
          edgeState = EDGE_STATE.USERINPUT;
        } else {
          edgeState = EDGE_STATE.ACTIVE;
        }
        let startX;
        let startY;
        let endX;
        let endY;
        const hor = sx === ex;
        const ver = sy === ey;

        // excluding cases where both points are same
        if ((hor && ver) || (!hor && !ver)) {
          return undefined;
        }

        // horizonal line
        if (hor) {
          startX = sx;
          endX = sx;
          if (sy > ey) {
            startY = ey;
            endY = sy;
          }
          if (sy < ey) {
            startY = sy;
            endY = ey;
          }
        }
        // vertical line
        if (ver) {
          startY = sy;
          endY = sy;
          if (sx > ex) {
            startX = ex;
            endX = sx;
          }
          if (sx < ex) {
            startX = sx;
            endX = ex;
          }
        }

        // check if all nodes are valid nodes
        if (
          areValid([startX, endX], nRows) &&
          areValid([startY, endY], nCols)
        ) {
          temp[startX][startY] = {
            ...temp[startX][startY],
            neigh: [
              ...temp[startX][startY].neigh,
              {
                state: edgeState,
                loc: [endX, endY],
                isCorrect: !isValuePresent(
                  [sx, sy],
                  [ex, ey],
                  solution,
                  isAutoCheck,
                ),
              },
            ],
          };
          temp[endX][endY] = {
            ...temp[endX][endY],
            neigh: [
              ...temp[endX][endY].neigh,
              {
                state: edgeState,
                loc: [startX, startY],
                isCorrect: !isValuePresent(
                  [sx, sy],
                  [ex, ey],
                  solution,
                  isAutoCheck,
                ),
              },
            ],
          };
        }
      },
    );
  }

  return temp;
};

// to check if a node has an active edge
export const isNodeActive = n =>
  Array.isArray(n?.neigh) &&
  n.neigh.some(neighbor => neighbor.state === EDGE_STATE.ACTIVE);

// to get neighbors from matrix
export const getNeighbors = ([ax, ay], mat) => mat[ax][ay]?.neigh || [];

// to check if two nodes are neighbor in a matrix
export const findNeighbor = ([ax, ay], [bx, by], mat) => {
  const index = mat?.[ax]?.[ay]?.neigh.findIndex(
    ({ loc }) => loc[0] === bx && loc[1] === by,
  );
  return isNaN(index) ? -1 : index;
};

// get an array of all numbers present in the matrix
export const getNumbers = mat => {
  let numbers = [];
  const nRow = mat?.length;
  const nCol = mat?.[0]?.length;
  if (!nRow || !nCol) {
    return;
  }

  mat.forEach((row, r) => {
    if (r >= nRow - 1) return;
    row.forEach((node, c) => {
      if (c >= nCol - 1) return;
      numbers = [
        ...numbers,
        ...(node.n > -1 ? [{ r: r + 1, c: c + 1, n: node.n }] : []),
      ];
    });
  });
  return numbers;
};

// get an array of all edges present in the matrix
export const getEdges = mat => {
  let edges = [];
  mat.forEach((row, sX) => {
    row.forEach((node, sY) => {
      let horEdges = [];
      let verEdges = [];
      node.neigh.forEach(edge => {
        const [eX, eY] = edge.loc;
        if (eX > sX && eY === sY) {
          verEdges = [
            ...verEdges,
            {
              a: [sX, sY],
              b: [eX, eY],
              ...(edge?.state === EDGE_STATE.NOT_ALLOWED
                ? { notAllowed: true }
                : {}),
              ...(edge?.state === EDGE_STATE.HOVERED ? { hovered: true } : {}),
            },
          ];
        } else if (eY > sY && eX === sX) {
          horEdges = [
            ...horEdges,
            {
              a: [sX, sY],
              b: [eX, eY],
              ...(edge?.state === EDGE_STATE.NOT_ALLOWED
                ? { notAllowed: true }
                : {}),
              ...(edge?.state === EDGE_STATE.HOVERED ? { hovered: true } : {}),
              ...(edge?.state === EDGE_STATE.USERINPUT
                ? { userInput: true }
                : {}),
            },
          ];
        }
      });
      edges = [...edges, ...horEdges, ...verEdges];
    });
  });
  return edges;
};

// get an array of all active edges present in the matrix
export const getActiveEdges = mat =>
  getEdges(mat).filter(edge => !edge?.notAllowed && !edge?.hovered);

export const createLoopDetector = () => {
  const graph = new Map();

  const addEdge = (nodeA, nodeB) => {
    if (!graph.has(nodeA)) graph.set(nodeA, []);
    if (!graph.has(nodeB)) graph.set(nodeB, []);
    graph.get(nodeA).push(nodeB);
    graph.get(nodeB).push(nodeA);
  };

  const detectCycle = () => {
    const visited = new Set();

    const dfs = (node, parent) => {
      visited.add(node);
      return (graph.get(node) || []).some(neighbor => {
        if (!visited.has(neighbor)) {
          return dfs(neighbor, node);
        }
        return neighbor !== parent;
      });
    };

    return Array.from(graph.keys()).some(
      node => !visited.has(node) && dfs(node, null),
    );
  };

  const checkForLoops = lines => {
    graph.clear();
    lines.forEach(line => {
      if (!line.notAllowed) {
        const pointA = `${line.a[0]},${line.a[1]}`;
        const pointB = `${line.b[0]},${line.b[1]}`;
        addEdge(pointA, pointB);
      }
    });
    return detectCycle();
  };

  const checkSolutionMatchesLines = (lines, solution) => {
    const lineSet = new Set(
      lines
        .filter(line => !line.notAllowed)
        .map(line => `${line.a[0]},${line.a[1]}-${line.b[0]},${line.b[1]}`),
    );

    return solution.every(line =>
      lineSet.has(`${line.a[0]},${line.a[1]}-${line.b[0]},${line.b[1]}`),
    );
  };

  return { checkForLoops, checkSolutionMatchesLines };
};
